package info.rezident.app.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.seo.*
import info.rezident.app.ui.theme.AppTheme
import info.rezident.app.ui.theme.AppTypography
import kotlinx.browser.document
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page("termeni")
@Composable
fun TermsAndConditionsPage() {
    val title = "Termeni și Condiții — rezident.info"
    val description =
        "Condiții de utilizare, eligibilitate, proprietate intelectuală, limitări, planuri și soluționarea disputelor."
    val canonicalUrl = "https://rezident.info/termeni"
    val ogImageUrl = "https://rezident.info/assets/og/og-image.png"

    Seo(title = title, description = description)

    LaunchedEffect(Unit) {
        document.setCanonicalUrl(canonicalUrl)
        document.setMetaNameTag(name = "robots", content = "index, follow")
        document.setMetaNameTag(name = "twitter:card", content = "summary_large_image")
        document.setMetaNameTag(name = "twitter:title", content = title)
        document.setMetaNameTag(name = "twitter:description", content = description)
        document.setMetaNameTag(name = "twitter:image", content = ogImageUrl)

        document.setOpenGraphTag(property = "og:title", content = title)
        document.setOpenGraphTag(property = "og:description", content = description)
        document.setOpenGraphTag(property = "og:url", content = canonicalUrl)
        document.setOpenGraphTag(property = "og:image", content = ogImageUrl)
    }

    val termsJsonLd: String = """
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://rezident.info/termeni",
          "name": "Termeni și Condiții - rezident.info",
          "inLanguage": "ro-RO",
          "dateModified": "2026-04-02"
        }
    """.trimIndent()

    ApplicationLdJson(termsJsonLd)

    Column(
        modifier = Modifier.fillMaxWidth().padding(32.px).gap(16.px).backgroundColor(AppTheme.colors.background),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(16.px)
    ) {
        SpanText("Termeni și Condiții de Utilizare", modifier = AppTypography.h1.toModifier())
        SpanText("Platformă: rezident.info", modifier = AppTypography.body1.toModifier())
        SpanText("Data intrării în vigoare: 2 aprilie 2026", modifier = AppTypography.body1.toModifier())
        SpanText(
            "Operator: MEȘINĂ ANDREI PERSOANĂ FIZICĂ AUTORIZATĂ",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("CUI: 54277907", modifier = AppTypography.body1.toModifier())
        SpanText("Nr. Reg. Comerțului: F2026014551006", modifier = AppTypography.body1.toModifier())
        SpanText(
            "Adresă: B-dul Bucureștii Noi, 136, et. Parter, ap. 5, Sector 1, București",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("1. Definiții", modifier = AppTypography.h2.toModifier())
        SpanText(
            "• Platforma – site-ul și aplicațiile mobile rezident.info.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "• Utilizator – persoana fizică ce își creează cont pe Platformă.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "• Quiz de Orientare – setul de întrebări care generează Recomandări de specialitate.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "• Recomandări – rezultate generate algoritmic pe baza răspunsurilor la Quiz (cu elemente blocate/ deblocate în funcție de starea accesului).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "• Plată – achiziții procesate prin Stripe, conform opțiunii afișate în aplicație.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("2. Acceptarea Termenilor", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Prin crearea unui cont sau utilizarea Platformei, declară că ai citit, înțeles și acceptat acești Termeni.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("3. Eligibilitate", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Serviciul este destinat studenților la Medicină și absolvenților care se pregătesc pentru examenul de rezidențiat. Trebuie să ai cel puțin 18 ani.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("4. Crearea contului", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Ești responsabil de confidențialitatea datelor de autentificare și de toate activitățile desfășurate în contul tău.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("5. Servicii și acces", modifier = AppTypography.h2.toModifier())
        Table(attrs = Modifier.fillMaxWidth().toAttrs()) {
            Thead {
                Tr {
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) { SpanText("Plan", modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)) }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText(
                            "Acces Quiz",
                            modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)
                        )
                    }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText(
                            "Recomandări",
                            modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)
                        )
                    }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) { SpanText("Durată", modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)) }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText(
                            "Preț/Plată*",
                            modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }
            Tbody {
                Tr {
                    Td { SpanText("Acces gratuit", modifier = AppTypography.body1.toModifier()) }
                    Td { SpanText("nelimitat", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "vizualizare limitată (cu elemente blocate)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td { SpanText("nelimitat", modifier = AppTypography.body1.toModifier()) }
                    Td { SpanText("0 lei", modifier = AppTypography.body1.toModifier()) }
                }
                Tr {
                    Td { SpanText("Analiză Detaliată", modifier = AppTypography.body1.toModifier()) }
                    Td { SpanText("nelimitat", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "deblocare recomandări complete pentru quiz-ul selectat",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td { SpanText("conform produsului ales", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "afișat în aplicație / procesat prin Stripe",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                }
            }
        }
        SpanText(
            "*Prețurile și disponibilitatea produselor pot fi modificate; valoarea finală, monedele și promoțiile sunt afișate în aplicație la momentul achiziției.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("6. Drepturi de proprietate intelectuală", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Conținutul Platformei, inclusiv algoritmul, este protejat de drepturi de autor. Primești o licență personală, neexclusivă, netransferabilă de a-l utiliza strict în scop personal, non-comercial.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("7. Limitarea răspunderii", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Platforma este furnizată „ca atare”, fără garanții de orice fel, explicite sau implicite. Recomandările sunt generate algoritmic și au rol strict informativ; nu reprezintă orientare profesională, consiliere academică sau juridică. Nu garantăm admiterea la o anumită specialitate, acuratețea absolută a conținutului sau lipsa erorilor. În măsura maximă permisă de lege, nu răspundem pentru pierderi, prejudicii, decizii sau rezultate obținute în urma utilizării Platformei.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("8. Utilizare interzisă", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Este interzisă: (a) copierea, redistribuirea sau vânzarea conținutului; (b) utilizarea de scripturi automate; (c) încercarea de inginerie inversă a algoritmului; (d) orice acțiune care afectează securitatea Platformei.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("9. Suspendare și încetare", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Putem suspenda sau închide contul dacă încalci Termenii, fără rambursarea sumelor plătite.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("10. Modificări ale Termenilor", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Orice modificare substanțială va fi publicată pe această pagină și/sau notificată în aplicație cu cel puțin 14 zile înainte de a intra în vigoare.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("11. Legea aplicabilă și soluționarea disputelor", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Termenii sunt guvernați de legea română. Litigiile se soluționează pe cale amiabilă; în caz contrar, competența revine instanțelor din București.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("12. Contact", modifier = AppTypography.h2.toModifier())
        SpanText("rezident.info/contact", modifier = AppTypography.body1.toModifier())
        SpanText("salut@rezident.info", modifier = AppTypography.body1.toModifier())
    }
}